import { z } from 'zod';
import { centAmount, isoDate, uuid } from './posting.js';
/**
 * Die Verträge der Zahldienste (Spec Zahldienste 6).
 *
 * **Alle Systemgrenzen mit Zod**, wie das Konzept es verlangt. Vier Formen: der
 * **Abrufauftrag** mit Zeitraum, der **Abrufbericht** mit Rändern, Seitenzahl,
 * übernommenen und doppelten Bewegungen und der Nichttrefferquote, der
 * **Deckungsbericht** — der steht in `bank.ts`, weil er derselbe Endpunkt ist
 * und nur um einen Abschnitt erweitert wurde — und der **Abgleichbericht**.
 *
 * **Die Beträge sind gebrandete Zeichenketten und keine Zahlen.** Hausregel 1
 * gilt auch im Vertrag, und hier zuerst: Was an der Systemgrenze durch eine
 * `number` läuft, ist danach nicht mehr zu retten.
 *
 * **Die 31 Tage sind PAYPALS Grenze und gelten für Mollie nicht** (Befund
 * `Z-molliegrenze`, entschieden am 2026-08-21). Gemessen am 2026-08-20
 * antwortet PayPal auf einen grösseren Zeitraum mit `400 INVALID_REQUEST`,
 * `issue: "Date range is greater than 31 days"`. Das ist ein `400` und kein
 * `429`: Ein Rückstau mit Verdopplung räumt ihn nie ab, und ein Aufrufer, der
 * es trotzdem versucht, sieht beschäftigt aus, während der Zeitraum ungedeckt
 * bleibt. Ein Auftrag darüber wird deshalb an **unserer** Grenze abgewiesen.
 *
 * **Bei Mollie wird nach dem Zeitraum gar nicht erst gefragt** (Befund
 * `Z-mollieohnezeitraum`, ausdrückliche Weisung des Auftraggebers vom
 * 2026-08-21). Die Bewegungsliste kennt keinen Zeitraumparameter — sie blättert
 * über einen Cursor über die **gesamte** Historie, und ein Von/Bis in der Maske
 * schränkte nicht ein, was geholt wird, sondern nur, was davon behalten wird.
 * Ein Mollie-Abruf holt deshalb immer alles, und der Auftrag dazu ist **leer**.
 *
 * **Der Saldobericht hat trotzdem eine Grenze, und sie ist am 2026-08-21
 * gemessen worden.** `GET /v2/balances/{id}/report` antwortet auf einen
 * Zeitraum über 180 Tage mit `422 Unprocessable Entity`, `detail:
 * "Cannot create balance report for 'from' and 'until' dates more than 180 days
 * apart."`, `field: "from"`. Halbierungssuche über die echte Historie: **180
 * Tage angenommen, 181 abgewiesen.** Die frühere Aussage dieser Datei — Mollie
 * habe „gemessen keine Längengrenze" — ist damit **widerlegt**; sie war an
 * Fenstern von dreissig Tagen gemessen und über die ganze Historie nie geprüft.
 *
 * **Die Folge trägt die ganze Ära:** «alles holen» ist bei Mollie keine EINE
 * Anfrage, sondern eine **Folge** von Fenstern zu höchstens
 * `MOLLIE_FENSTER_TAGE` Tagen — und jedes bekommt damit seine eigene
 * Saldoklammer. Der Preis, den der Auftraggeber angenommen hatte (eine einzige
 * Klammer über alles, die bei einem Bruch nicht mehr sagt **wo**), fällt damit
 * nicht an; er wäre auch nicht bezahlbar gewesen (Messung in
 * `apps/api/src/zahldienste/quellen.ts`).
 *
 * **Und die Grenze hängt trotzdem nicht an einer Angabe IM Auftrag.** Der
 * Auftrag trägt weiterhin **keinen** `provider`, und das ist keine
 * Nachlässigkeit, sondern die tragende Begründung von
 * `fetchWindowRequestSchema`: Welcher Dienst gefragt wird, hängt am Konto: ein
 * Auftrag, der ihn mitbrächte, erlaubte den Widerspruch zwischen
 * «PayPal-Konto» und «hole bei Mollie». Geprüft wird die Länge deshalb **an der
 * Systemgrenze mit dem Konto in der Hand** — im Endpunkt, der das Konto ohnehin
 * geladen hat, über `fensterZuLang()`.
 */
/** Welcher Dienst — dieselben zwei Werte wie `bank_account.provider`. */
export const zahldienstSchema = z.enum(['paypal', 'mollie']);
/** Der Zustand eines Abruffensters, wie `bank_statement_import.fetch_status` ihn führt. */
export const fetchStatusSchema = z.enum(['uebernommen', 'verworfen', 'gescheitert']);
/**
 * Die Lage der Saldoklammer.
 *
 * **Drei Werte und nicht zwei.** «Noch nicht klammerbar» ist kein Bruch und
 * kein Aufgehen, sondern die Auskunft, dass der Tag Vorlauf aus `E-215` noch
 * nicht um ist. Wer ihn mit `gebrochen` zusammenzöge, meldete jeden frischen
 * Abruf als Befund.
 */
export const clampStateSchema = z.enum(['stimmig', 'gebrochen', 'noch_nicht_klammerbar']);
const TAG_MS = 86_400_000;
/**
 * Die längste Zeitspanne eines Abruffensters **je Dienst**, in Tagen,
 * einschliessend gezählt. `null` hiesse: keine Grenze — und es gibt keinen
 * Dienst mehr, für den das stimmt.
 *
 * | Dienst | Grenze | gemessen |
 * |---|---|---|
 * | `paypal` | **31** | `400 INVALID_REQUEST`, `issue: "Date range is greater than 31 days"`, am 2026-08-20 |
 * | `mollie` | **180** | `422 Unprocessable Entity`, `detail: "Cannot create balance report for 'from' and 'until' dates more than 180 days apart."`, `field: "from"`, am 2026-08-21 |
 *
 * **Hier stand für Mollie bis zum 2026-08-21 `null`, und das war falsch**
 * (Befund `Z-mollieohnezeitraum`). Der Satz „der Saldobericht hat über die
 * gesamte Historie keine Länge abgewiesen" war nie an der ganzen Historie
 * gemessen, sondern an Fenstern von dreissig Tagen. Der erste wirkliche Versuch
 * — 2022-02-27 bis 2026-08-20, also 1636 Tage — wurde mit `422` abgewiesen; die
 * Halbierungssuche darüber ergab **180 angenommen, 181 abgewiesen**.
 *
 * **Die zwei Grenzen sind trotzdem nicht dasselbe.** PayPals 31 Tage begrenzen
 * das, was ein Anwender **beauftragen** darf; Mollies 180 Tage begrenzen, was
 * ein **Bericht** umfassen darf, und bei Mollie beauftragt niemand einen
 * Zeitraum (`fragtNachZeitraum()`). Die Zahl steht hier, weil sie die Länge
 * ist, in die der Historienabruf schneidet — nicht, weil sie eine Frage
 * beantwortet, die jemand gestellt hätte.
 */
export const FENSTER_MAX_TAGE_JE_DIENST = {
    paypal: 31,
    mollie: 180,
};
/**
 * In welche Fensterlänge der Mollie-Historienabruf schneidet, in Tagen.
 *
 * **Einer unter der gemessenen Grenze**, aus demselben Grund wie `FENSTER_TAGE`
 * bei PayPal einer unter 31 liegt: Der Bericht rechnet in `Europe/Amsterdam`,
 * die Fenstergrenzen entstehen über UTC-Mitternacht, und eine
 * Zeitzonenverschiebung soll die Grenze nicht von innen berühren. Der Preis ist
 * bei 1636 Tagen Historie **null zusätzliche Fenster** (zehn so wie so).
 */
export const MOLLIE_FENSTER_TAGE = 179;
/**
 * Fragt die Maske dieses Dienstes nach einem Zeitraum?
 *
 * **Nein heisst hier: die Frage wird nicht gestellt** — kein ausgegrautes Feld,
 * keine Vorbelegung, die man ändern könnte. Bei Mollie gibt es auf die Frage
 * keine Antwort, die etwas bewirkte: Die Bewegungsliste kennt keinen
 * Zeitraumparameter, ein Von/Bis schränkte allein ein, was von der ohnehin
 * vollständig gelesenen Historie behalten wird.
 *
 * **Eine Funktion und keine zweite Tabelle in der Oberfläche.** Die Maske, der
 * Endpunkt und die Verträge müssen sich hier einig sein; drei Auslegungen
 * derselben Frage wären drei Gelegenheiten, sie verschieden zu beantworten.
 */
export function fragtNachZeitraum(provider) {
    return provider === 'paypal';
}
/** Die Länge eines Fensters in Tagen, einschliessend an beiden Rändern. */
export function fensterlaenge(from, to) {
    const von = Date.parse(`${from}T00:00:00Z`);
    const bis = Date.parse(`${to}T00:00:00Z`);
    if (Number.isNaN(von) || Number.isNaN(bis))
        return Number.NaN;
    return Math.floor((bis - von) / TAG_MS) + 1;
}
/**
 * Ist das Fenster für diesen Dienst zu lang? Gibt die Tageszahl zurück, wenn
 * ja, und `undefined`, wenn nein.
 *
 * **Die Zahl und nicht ein `true`.** Eine Ablehnung, die nicht sagt, wie lang
 * das Fenster war, zwingt den Aufrufer zum Nachrechnen — und er rechnet es
 * anders, sobald ein Schaltjahr im Spiel ist.
 *
 * **Für Mollie beantwortet diese Funktion eine Frage, die niemand stellt.**
 * Ein Mollie-Abruf trägt keinen Zeitraum (`fragtNachZeitraum()`); die Grenze
 * von 180 Tagen begrenzt den **Bericht** und wird vom Historienabruf über
 * `MOLLIE_FENSTER_TAGE` eingehalten. Sie steht hier trotzdem und nicht als
 * Sonderfall daneben: Ein Auftrag, der aus einer alten Maske oder einem Skript
 * doch mit Von und Bis ankommt, soll an **unserer** Grenze abgewiesen werden
 * und nicht mit `422` beim Anbieter.
 */
export function fensterZuLang(provider, from, to) {
    const grenze = FENSTER_MAX_TAGE_JE_DIENST[provider];
    if (grenze === null)
        return undefined;
    const tage = fensterlaenge(from, to);
    return Number.isNaN(tage) || tage <= grenze ? undefined : tage;
}
/**
 * Ein Abrufauftrag: welcher Zeitraum geholt werden soll.
 *
 * **Kein `provider` im Auftrag.** Welcher Dienst gefragt wird, hängt am Konto
 * und nicht an der Anfrage — ein Auftrag, der ihn mitbrächte, erlaubte den
 * Widerspruch zwischen «PayPal-Konto» und «hole bei Mollie», und die Antwort
 * darauf müsste eine Fehlermeldung sein, die es nicht zu geben braucht.
 */
export const fetchWindowRequestSchema = z
    .object({
    from: isoDate,
    to: isoDate,
})
    .strict()
    .transform((wert, ctx) => {
    const von = Date.parse(`${wert.from}T00:00:00Z`);
    const bis = Date.parse(`${wert.to}T00:00:00Z`);
    if (Number.isNaN(von) || Number.isNaN(bis) || bis < von) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['to'],
            message: 'Das Fensterende liegt vor seinem Anfang.',
        });
        return z.NEVER;
    }
    // **Die Längengrenze steht hier NICHT mehr** (Befund `Z-molliegrenze`).
    // Sie hängt am Dienst, der Auftrag trägt keinen — und ein Auftrag, der ihn
    // mitbrächte, erlaubte den Widerspruch zwischen Konto und Anfrage. Geprüft
    // wird sie über `fensterZuLang()` dort, wo das Konto bekannt ist.
    return wert;
});
/**
 * Der Auftrag für einen Mollie-Abruf: **leer**, und zwar mit Absicht.
 *
 * **`.strict()` ist hier die ganze Zusicherung.** Ein `from` oder ein `to` an
 * einem Mollie-Konto wird abgewiesen statt stillschweigend übergangen. Der
 * Unterschied ist nicht Pedanterie: Ein übergangener Zeitraum sähe für den
 * Aufrufer aus wie ein eingehaltener — er bekäme die ganze Historie und
 * glaubte, er habe einen Monat geholt.
 *
 * **Ein fehlender Körper ist derselbe Auftrag wie ein leerer.** `POST` ohne
 * Körper ist die natürliche Form für «hole alles»; wer `{}` schickt, meint
 * dasselbe.
 */
export const historienauftragSchema = z.object({}).strict();
/**
 * Über welchen Weg ein Anschlussbruch gefunden wurde (`E-214`, `E-363`).
 *
 * **`angrenzend`**: der Vergleich lief gegen den unmittelbaren Nachbarn — die
 * Zeiträume grenzen an (oder eine Lücke klafft), und/oder die Randsalden
 * brechen. **`ueberlappend`**: zwei Fenster überlappen sich, und das Fenster,
 * das am Vortag des Beginns endet, trägt einen anderen Endsaldo, als das
 * überlappende Fenster als Anfangssaldo angibt. Beides ist ein Befund und
 * kein Abbruch — Überlappung ist beim Nachholen der Normalfall, nur der Saldo
 * am Übergang widerspricht sich.
 */
export const anschlusspruefwegSchema = z.enum(['angrenzend', 'ueberlappend']);
/** Ein Bruch zwischen zwei Abruffenstern (`E-214`, `E-363`). */
export const fetchContinuityBreakSchema = z
    .object({
    pruefweg: anschlusspruefwegSchema,
    afterToDate: isoDate,
    afterClosingBalance: centAmount,
    beforeFromDate: isoDate,
    beforeOpeningBalance: centAmount,
})
    .strict();
/**
 * Der Bericht eines Abrufs.
 *
 * **Beide Randsalden sind Pflicht und die Nichttrefferquote auch.** Sie sind
 * die drei Zahlen, an denen sich der Abruf nachrechnen lässt: Ohne die Ränder
 * ist die Klammer nicht nachvollziehbar, ohne die Quote sieht ein Fenster mit
 * hundert Nichttreffern aus wie eines mit keinem.
 *
 * **`clamp` und `fetchStatus` sagen verschiedene Dinge.** Ein Fenster mit
 * `clamp: 'gebrochen'` trägt `fetchStatus: 'verworfen'` — gelesen und
 * verworfen. Ein Fenster mit `fetchStatus: 'gescheitert'` hat gar keine
 * Klammer, weil nichts Lesbares kam. Wer die zwei Felder zusammenzöge,
 * verlöre genau diese Unterscheidung, und der Deckungsbericht baut auf ihr.
 */
export const fetchReportSchema = z
    .object({
    importId: uuid,
    bankAccountId: uuid,
    provider: zahldienstSchema,
    from: isoDate,
    to: isoDate,
    /**
     * Wann der Lauf stattgefunden hat — **nicht** der Zeitraum, den er deckt.
     *
     * Die zwei werden verwechselt, und die Kontoansicht braucht beide: Spec
     * 13.2 verlangt „letzter erfolgreicher Abruf, mit Zeitpunkt und dem
     * Zeitraum, den er gedeckt hat". Ein Konto, dessen letzter Abruf vor neun
     * Tagen fehlgeschlagen ist, sieht ohne diese Angabe genauso aus wie eines,
     * das gestern grün lief. Es ist `bank_statement_import.imported_at`, als
     * ISO-Zeichenkette wie `bankStatementSchema.importedAt` — nicht als `Date`,
     * denn eine Systemgrenze führt keine Objekte mit Zeitzonenverhalten.
     */
    fetchedAt: z.string().min(1),
    fetchStatus: fetchStatusSchema,
    /**
     * Der vom Anbieter gerechnete Randwert am Anfang des Fensters — **`null`,
     * wenn keiner gelesen werden konnte** (Befund `Z-zeilenistkeinauszug`,
     * 2026-08-21).
     *
     * **Vorher stand hier eine `0.00`, und die war eine Behauptung.** Die
     * Abfrage schrieb `money(r.opening_balance ?? '0.00')`, und die Oberfläche
     * zeigte für zehn gescheiterte PayPal-Fenster einen Anfangs- und Endsaldo
     * von null Euro. Ein Saldo von null ist eine **Aussage** — «der Topf war
     * leer» —, und genau die macht ein Fenster nicht, dessen Abruf keine
     * lesbare Antwort bekommen hat. `bank_statement_import` führt beide Spalten
     * seit Migration `0038` nullbar und verlangt sie über
     * `bank_statement_import_zahlen_bei_uebernahme` genau für die übernommenen
     * Zeilen; dieser Vertrag zieht das nach.
     */
    openingBalance: centAmount.nullable(),
    /** Der vom Anbieter gerechnete Randwert am Ende; `null` wie oben. */
    closingBalance: centAmount.nullable(),
    /** Die Zahl der gelesenen Seiten beziehungsweise Cursorschritte. */
    pageCount: z.number().int().nonnegative(),
    /**
     * Die Zahl der archivierten Antworten — mindestens eine für ein Fenster
     * des Abrufwegs (`E-226`), **null zulässig für ein Fenster des
     * Dateiwegs** (`source = 'paypal_datei'`, Migration `0055`, `E-604`): Ein
     * Dateiimport archiviert die Datei selbst als Beleg und schreibt keine
     * `payment_fetch_response`-Zeile — `positive()` behauptete das für jedes
     * Fenster, bevor der Dateiweg im Deckungsbericht überhaupt sichtbar war.
     */
    responseCount: z.number().int().nonnegative(),
    /** Gelesene Bewegungen, **einschliesslich** der bereits bekannten. */
    bookedCount: z.number().int().nonnegative(),
    /** Zeilen ohne Wirkung auf den Euro-Saldo. */
    skippedCount: z.number().int().nonnegative(),
    duplicateCount: z.number().int().nonnegative(),
    duplicateAmount: centAmount,
    /** Der Anteil der Nichttreffer, vier Nachkommastellen (`E-228`). */
    missRate: z.number().min(0).max(1),
    clamp: clampStateSchema,
    /**
     * Stammt diese Zeile aus der Frischschiene des Zahldiensttakts
     * (`Z-frischtakt`, 2026-08-26)?
     *
     * **`true` heisst: nie bewiesen und nie als gedeckt gezählt.** Eine
     * frische Zeile trägt zwar `fetchStatus: 'uebernommen'`, ihre Saldoklammer
     * ist aber nie gerechnet worden — ihr Fenster reicht bis heute und ist
     * damit nie alt genug (`E-215`). Sie zählt nirgends als
     * Vollständigkeitsnachweis (`E-211`), auch nicht in `continuity` daneben:
     * Eine frische Zeile überlappt den gedeckten Rand mit Absicht und grenzt
     * deshalb nicht exakt an ihre Nachbarn.
     *
     * **`false` ist der Regelfall** — die klammerbare Schiene und jeder
     * Handabruf. `false` heisst nicht «bewiesen»; das sagt weiterhin `clamp`.
     */
    frisch: z.boolean(),
    /**
     * **Warum** das Fenster nicht übernommen worden ist — `null` bei jedem
     * übernommenen Fenster (`E-267`).
     *
     * **Pflichtfeld und nullbar, nicht optional.** Ein Feld, das weggelassen
     * werden darf, wird weggelassen, und dann steht in der Oberfläche wieder
     * „gescheitert" ohne einen Grund. Genau das hat der Erstlauf vom
     * 2026-08-21 hinterlassen: 63 Zeilen, die sagen, **dass** etwas schiefging,
     * und keine, die sagt **was**.
     *
     * `null` heisst hier zweierlei, und das ist die benannte Grenze: entweder
     * ist das Fenster übernommen — dann ist es die Wahrheit —, oder es stammt
     * aus einem Lauf **vor** Migration `0050`. Die 63 Zeilen des Fehllaufs
     * sind der zweite Fall, und `bank_statement_import` kennt kein `UPDATE`;
     * sie schweigen für immer.
     */
    failure: z
        .object({
        /** Der Fehlercode der Anwendung, etwa `PAY_CLAMP_BROKEN`. */
        code: z.string().min(1),
        /** Der deutsche Klartext, ohne Zugangsschlüssel. */
        detail: z.string().min(1),
    })
        .strict()
        .nullable(),
    /**
     * Der Anschluss an die Nachbarfenster — **ein Befund und kein Abbruch**
     * (`E-214`, `E-363`). `ok: false` heisst nicht, dass der Import
     * gescheitert ist.
     */
    continuity: z
        .object({
        ok: z.boolean(),
        breaks: z.array(fetchContinuityBreakSchema),
        /**
         * Überlappt dieses Fenster mit einem Vorgänger, dessen Saldo sich
         * mangels Vortagsfenster **nicht prüfen** liess? **Kein Bruch** —
         * `ok` bleibt davon unberührt —, aber eine eigene Auskunft: Ohne sie
         * sähe ein ungeprüfter Anschluss in der Oberfläche wie ein geprüfter,
         * lückenloser aus, und das wäre eine Behauptung, die niemand geprüft
         * hat (`E-363`).
         */
        ueberlappungNichtPruefbar: z.boolean(),
    })
        .strict(),
})
    .strict();
/**
 * Ein Zeitraum, den ein Abruflauf **übersprungen** hat, weil er bereits
 * `übernommen` war — die Kehrseite von `fetchReportSchema` (`W-uebersprungen`,
 * `W-gefilterte-zahl`, beide 2026-08-21).
 *
 * **War bisher nur eine anonyme Form am Endpunkt selbst.** `POST
 * :id/fetches` gab `{ from, to }` zurück, ohne dass ein Vertrag diese Form
 * benannte — anders als sein Geschwister `fetches: FetchReport[]` in
 * derselben Antwort. Ein Historienlauf, der neun von zehn Abschnitten
 * überspringt und den zehnten verwirft, meldete ohne diese Liste genau einen
 * verworfenen Abschnitt und sah aus wie ein vollständig gescheiterter Lauf.
 *
 * **Nur ein Grund existiert heute — bereits übernommen —, deshalb kein
 * eigenes `grund`-Feld.** Ein verworfenes oder gescheitertes Fenster wird nie
 * übersprungen, sondern erneut geholt (`HistorienabrufService.ganzeHistorie`);
 * ein zweiter Wert für `grund` hätte hier nichts zu unterscheiden. Käme ein
 * zweiter Überspringungsgrund hinzu, gehörte er hierher und nicht in eine
 * zweite, parallele Form.
 */
export const uebersprungenesFensterSchema = z
    .object({
    /** Erster Tag des übersprungenen Fensters, einschliessend. */
    from: isoDate,
    /** Letzter Tag des übersprungenen Fensters, einschliessend. */
    to: isoDate,
})
    .strict();
/**
 * Die Antwort auf «Bewegungen abrufen» — Ergänzung 3 aus Spec Zahldienste 13.
 *
 * **`skipped` ist Pflicht und nicht Zierrat** (Befund `W-gefilterte-zahl`,
 * 2026-08-21, siehe `uebersprungenesFensterSchema`). Bei einem einzelnen
 * Fensterabruf (PayPal) ist die Liste leer; bei einem Historienlauf (Mollie)
 * trägt sie jeden bereits übernommenen Abschnitt. Die Form bleibt für beide
 * Dienste dieselbe — eine Antwort, die je nach Dienst wechselt, ist die
 * Stelle, an der ein Aufrufer den zweiten Fall vergisst.
 */
export const fetchListSchema = z
    .object({
    // `.readonly()` auf beiden Listen: `HistorienabrufService.ganzeHistorie()`
    // und `ZahldiensteQuery` liefern `readonly FetchReport[]`, und ein
    // mutables Array hier liesse den Rueckgabetyp des Controllers an genau
    // dieser Stelle scheitern (Vorbild `formatReportSchema.sprossen`).
    fetches: z.array(fetchReportSchema).readonly(),
    skipped: z.array(uebersprungenesFensterSchema).readonly(),
})
    .strict();
/**
 * Die Liste der bisherigen Abruffenster — `GET :id/fetches`.
 *
 * **Ein anderes Ding als `fetchListSchema`, und deshalb ein eigenes Schema.**
 * `GET :id/fetches` beantwortet «was ist bisher geholt worden»; nur `POST
 * :id/fetches` kann etwas überspringen. Ein gemeinsames Schema zwänge den
 * Lesepfad zu einem leeren `skipped`, das nichts über diesen Aufruf aussagt.
 */
export const fetchWindowListSchema = z
    .object({
    fetches: z.array(fetchReportSchema).readonly(),
})
    .strict();
/**
 * Der Abgleichbericht — K4, und **K4 ist in Stufe A ohne Aussage**.
 *
 * Der Bericht wird gebaut, weil Stufe B hier ohne Umbau anschliesst. Und er
 * sagt **selbst**, dass er in Stufe A nichts bedeutet: Auf unserer Seite steht
 * null, weil nichts gebucht wird, und zwei Nullen sind nicht dasselbe wie
 * Übereinstimmung.
 *
 * **`meaningful` ist Pflicht und nicht optional.** Ein Feld, das weggelassen
 * werden darf, wird weggelassen — und dann liest jemand die Differenz als
 * Aussage. Der Grund steht im Klartext daneben, damit niemand die Bedeutung
 * eines `false` nachschlagen muss.
 */
export const reconciliationReportSchema = z
    .object({
    bankAccountId: uuid,
    provider: zahldienstSchema,
    /** Der Stichtag, auf den beide Seiten gerechnet sind. */
    asOf: isoDate,
    /** Der Saldo laut Anbieter. */
    providerBalance: centAmount,
    /** Der Saldo laut Journal — in Stufe A ausnahmslos `0.00`. */
    journalBalance: centAmount,
    difference: centAmount,
    /** `false` in Stufe A. Der Endpunkt setzt ihn und nicht der Leser. */
    meaningful: z.boolean(),
    reason: z.string().min(1),
})
    .strict();
/**
 * Die Lage eines geprüften Endpunkts.
 *
 * **Vier Werte und nicht zwei, und der Unterschied zwischen `gesperrt` und
 * `kein_zugang` ist der ganze Anlass dieser Probe.** Gemessen am 2026-08-20:
 * Der zuerst abgelegte Mollie-Schlüssel war **gültig** — `/v2/payments`
 * antwortete mit `200` —, während `/v2/balances`, `/v2/settlements`,
 * `/v2/organizations/me` und `/v2/invoices` mit `403` antworteten, weil der
 * Zugang auf ein Zahlungsprofil beschränkt war. Gesperrt war damit genau der
 * Teil, auf dem der Vollständigkeitsnachweis dieser Ära steht.
 *
 * Eine Probe, die beides als «geht nicht» zeigt, hätte diesen Fall nicht
 * aufgedeckt: Ein fehlender Schlüssel bricht beim Start und ist damit sichtbar,
 * bevor irgendetwas läuft; ein profilgebundener kommt durch die Anmeldung und
 * verweigert die Klammer.
 */
export const zugangslageSchema = z.enum([
    /** Der Anbieter hat geantwortet, und der Zugang darf hier lesen. */
    'erreichbar',
    /** Angemeldet, aber **ohne Recht** — der gemessene Fall (`403`). */
    'gesperrt',
    /** Die Anmeldung selbst ist abgelehnt (`401`) oder das Token kam nicht. */
    'kein_zugang',
    /** Weder das eine noch das andere: Netz, Zeitgrenze, unerwarteter Status. */
    'unerreichbar',
]);
/** Ein geprüfter Endpunkt. */
export const zugangspunktSchema = z
    .object({
    /**
     * Der Pfad, wie er in Spec 13.2 steht — **nie ein Wert**.
     *
     * Es gilt die Regel aus Spec 10 unverändert: kein Schlüssel, kein Präfix,
     * keine Länge. Eine Länge ist bei einem kurzen Geheimnis bereits eine
     * Auskunft.
     */
    endpunkt: z.string().min(1),
    /** Wofür dieser Endpunkt in dieser Ära gebraucht wird, im Klartext. */
    zweck: z.string().min(1),
    lage: zugangslageSchema,
    /** Der Status des Anbieters; `null` bei einem Netzfehler oder Zeitablauf. */
    status: z.number().int().nullable(),
})
    .strict();
/**
 * Das Ergebnis der Rechteprobe.
 *
 * **Sie schreibt nichts**: kein Fenster, keine Bewegung, keine archivierte
 * Antwort. Sie ist eine Auskunft über den Zugang und kein Abruf — und sie
 * ersetzt den Deckungsbericht nicht. Der sagt, welche Zeiträume **fehlen**; die
 * Probe sagt, ob der Zugang morgen noch trägt.
 */
export const zugangsprobeSchema = z
    .object({
    bankAccountId: uuid,
    provider: zahldienstSchema,
    gepruefteAm: z.string().min(1),
    /**
     * Die **Namen** der Umgebungsvariablen, aus denen der Zugang kommt — und
     * niemals ihr Inhalt (`E-225`, Spec 10).
     */
    umgebungsvariablen: z.array(z.string().min(1)),
    /** Je Endpunkt eine Zeile, in der Reihenfolge aus Spec 13.2. */
    punkte: z.array(zugangspunktSchema),
    /** Der Gesamtbefund in einem Satz — deutsch, ohne Wert und ohne Code. */
    befund: z.string().min(1),
    /**
     * Was der Betreiber tun muss, damit die Sperre verschwindet — ein Satz in
     * Kundensprache, oder `null`, wenn es nichts zu tun gibt oder die Ursache
     * nicht beim Recht liegt (kein Zugang, keine Antwort). **Ein eigenes Feld**
     * und nicht Teil des Befundsatzes: Der Befund sagt, was gemessen wurde, die
     * Handlung, was zu tun ist; die Oberfläche setzt sie als eigenen Hinweis ab.
     */
    handlung: z.string().min(1).nullable(),
})
    .strict();
//# sourceMappingURL=zahldienste.js.map